import React, { useState, useMemo } from 'react';
import {
  useGetAdsQuery,
  useGetCountriesQuery,
  useGetCitiesQuery,
} from '../../../store/api/adminApi';
import BarChart from '../../../components/charts/BarChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faArrowTrendDown,
  faSackDollar,
  faLayerGroup,
  faTag,
  faGlobe,
  faCity,
  faSyncAlt,
} from '@fortawesome/free-solid-svg-icons';

const PERIOD_OPTIONS = [
  { value: 'last_7_days',  label: 'Last 7 Days'  },
  { value: 'last_30_days', label: 'Last 30 Days'  },
  { value: 'last_90_days', label: 'Last 90 Days'  },
  { value: 'this_year',    label: 'This Year'     },
  { value: 'all_time',     label: 'All Time'      },
];

const COLORS = [
  'rgba(57,106,84,0.85)',
  'rgba(248,168,36,0.85)',
  'rgba(59,130,246,0.75)',
  'rgba(168,85,247,0.75)',
  'rgba(239,68,68,0.75)',
  'rgba(100,116,139,0.70)',
  'rgba(34,197,94,0.75)',
  'rgba(249,115,22,0.75)',
];

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${parseFloat((n / 1_000_000).toFixed(1))}M`;
  if (n >= 1_000)     return `$${parseFloat((n / 1_000).toFixed(1))}k`;
  return `$${n.toFixed(0)}`;
}

function getPeriodStart(period: string): Date | null {
  const now = new Date();
  switch (period) {
    case 'last_7_days':  { const d = new Date(now); d.setDate(d.getDate() - 7);  return d; }
    case 'last_30_days': { const d = new Date(now); d.setDate(d.getDate() - 30); return d; }
    case 'last_90_days': { const d = new Date(now); d.setDate(d.getDate() - 90); return d; }
    case 'this_year':    return new Date(now.getFullYear(), 0, 1);
    default:             return null;
  }
}

function groupByRevenue(
  ads: any[],
  keyFn: (ad: any) => string | undefined
): Array<{ label: string; revenue: number; count: number }> {
  const map = new Map<string, { revenue: number; count: number }>();
  ads.forEach((ad) => {
    const key = keyFn(ad);
    if (!key) return;
    const existing = map.get(key);
    const amount = Number(ad.payment_amount ?? 0);
    if (existing) {
      existing.revenue += amount;
      existing.count += 1;
    } else {
      map.set(key, { revenue: amount, count: 1 });
    }
  });
  return Array.from(map.entries())
    .map(([label, v]) => ({ label, ...v }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);
}

// --- Skeleton ---
const SkeletonBox: React.FC<{ width?: string; height?: number; className?: string }> = ({
  width = '100%', height = 16, className = '',
}) => (
  <div
    className={className}
    style={{
      width,
      height,
      borderRadius: 6,
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.4s infinite',
    }}
  />
);

// --- Stat Card ---
interface StatCardProps {
  label: string;
  value: string;
  change?: number | null;
  icon: any;
  iconColor?: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, change, icon, iconColor = '#396A54', loading }) => (
  <div className="col-6 col-xl-3">
    <div className="card h-100">
      <div className="card-body p-6">
        {loading ? (
          <>
            <SkeletonBox width="60%" height={20} className="mb-4" />
            <SkeletonBox width="50%" height={32} className="mb-2" />
            <SkeletonBox width="70%" height={14} />
          </>
        ) : (
          <>
            <div className="d-flex align-items-start justify-content-between mb-4">
              <div className="d-flex flex-column gap-1">
                {change !== null && change !== undefined ? (
                  <span
                    className="badge fw-semibold fs-8 d-inline-flex align-items-center gap-1"
                    style={{
                      background: change >= 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                      color: change >= 0 ? '#16a34a' : '#dc2626',
                      width: 'fit-content',
                    }}
                  >
                    <FontAwesomeIcon icon={change >= 0 ? faArrowTrendUp : faArrowTrendDown} style={{ fontSize: 10 }} />
                    {change >= 0 ? '+' : ''}{change}%
                  </span>
                ) : (
                  <span style={{ height: 22, display: 'block' }} />
                )}
              </div>
              <div
                className="d-flex align-items-center justify-content-center rounded"
                style={{ width: 40, height: 40, background: `${iconColor}18`, flexShrink: 0 }}
              >
                <FontAwesomeIcon icon={icon} style={{ color: iconColor, fontSize: 18 }} />
              </div>
            </div>
            <div className="fw-bold text-dark mb-1" style={{ fontSize: 28, lineHeight: 1 }}>{value}</div>
            <div className="text-muted fw-semibold fs-7">{label}</div>
          </>
        )}
      </div>
    </div>
  </div>
);

// --- Revenue Table ---
interface RevenueTableProps {
  title: string;
  subtitle: string;
  icon: any;
  rows: Array<{ label: string; revenue: number; count?: number }>;
  emptyText: string;
  loading?: boolean;
}

const RevenueTable: React.FC<RevenueTableProps> = ({ title, subtitle, icon, rows, emptyText, loading }) => {
  const total = rows.reduce((s, r) => s + r.revenue, 0);
  return (
    <div className="card mb-6">
      <div className="card-header pt-5 border-bottom-0">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold text-dark fs-4 d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={icon} style={{ color: '#396A54' }} />
            {title}
          </span>
          <span className="text-muted mt-1 fw-semibold fs-7">{subtitle}</span>
        </h3>
      </div>
      <div className="card-body pt-2">
        {loading ? (
          <div className="d-flex flex-column gap-3 py-4">
            <SkeletonBox height={200} />
            {[1,2,3].map(i => <SkeletonBox key={i} height={36} />)}
          </div>
        ) : rows.length > 0 ? (
          <>
            <div style={{ height: 260, marginBottom: 24 }}>
              <BarChart
                labels={rows.map(r => r.label)}
                datasets={[
                  {
                    label: 'Gross Revenue',
                    data: rows.map(r => r.revenue),
                    backgroundColor: rows.map((_, i) => COLORS[i % COLORS.length]),
                    borderColor: rows.map((_, i) => COLORS[i % COLORS.length]),
                    borderRadius: 4,
                  },
                ]}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (ctx: any) => ` ${formatCurrency(ctx.raw)}`,
                      },
                    },
                  },
                  scales: {
                    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
                    y: {
                      beginAtZero: true,
                      grid: { color: 'rgba(0,0,0,0.05)' },
                      ticks: { font: { size: 11 }, callback: (v: any) => formatCurrency(v) },
                    },
                  },
                }}
              />
            </div>
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3">
                <thead>
                  <tr className="border-0">
                    <th className="p-0 w-40px text-end text-muted fw-semibold fs-7 text-uppercase">#</th>
                    <th className="p-0 ps-4 text-muted fw-semibold fs-7 text-uppercase">Name</th>
                    {rows[0]?.count !== undefined && (
                      <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">Ads</th>
                    )}
                    <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">Revenue</th>
                    <th className="p-0 text-muted fw-semibold fs-7 text-uppercase">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const share = total > 0 ? ((row.revenue / total) * 100).toFixed(1) : '0';
                    return (
                      <tr key={i}>
                        <td className="text-end pe-4">
                          <span className="text-dark fw-bold fs-6">{i + 1}</span>
                        </td>
                        <td className="ps-4">
                          <div className="d-flex align-items-center gap-2">
                            <span
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: COLORS[i % COLORS.length],
                                flexShrink: 0,
                              }}
                            />
                            <span className="text-dark fw-bold fs-6">{row.label}</span>
                          </div>
                        </td>
                        {row.count !== undefined && (
                          <td>
                            <span className="text-muted fw-semibold fs-7">{row.count.toLocaleString()}</span>
                          </td>
                        )}
                        <td>
                          <span className="text-dark fw-bold fs-6">{formatCurrency(row.revenue)}</span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="progress h-6px w-60px bg-light-primary">
                              <div
                                className="progress-bar"
                                style={{ width: `${share}%`, background: COLORS[i % COLORS.length] }}
                              />
                            </div>
                            <span className="text-muted fs-8">{share}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="d-flex align-items-center justify-content-center py-16">
            <span className="text-muted fs-7">{emptyText}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Main Page ---
const AdminFinancialReport: React.FC = () => {
  const [countryId, setCountryId] = useState('');
  const [cityId,    setCityId]    = useState('');
  const [period,    setPeriod]    = useState('last_30_days');

  const { data: adsData, isLoading, isFetching, refetch } = useGetAdsQuery({ per_page: 5000 });
  const { data: countriesData } = useGetCountriesQuery({ per_page: 200 });
  const { data: citiesData }    = useGetCitiesQuery(
    countryId ? { country_id: countryId, per_page: 500 } : { per_page: 500 }
  );

  const countries: any[] = countriesData?.data?.data || countriesData?.data || [];
  const cities: any[]    = citiesData?.data?.data    || citiesData?.data    || [];

  const allAds: any[] = adsData?.data?.data || [];

  // Filter by period and location client-side
  const filteredAds = useMemo(() => {
    const periodStart = getPeriodStart(period);
    return allAds.filter((ad) => {
      if (periodStart) {
        const created = new Date(ad.created_at);
        if (created < periodStart) return false;
      }
      if (countryId && String(ad.user?.country_id) !== String(countryId)) return false;
      if (cityId    && String(ad.user?.city_id)    !== String(cityId))    return false;
      return true;
    });
  }, [allAds, period, countryId, cityId]);

  const grossRevenue  = useMemo(() => filteredAds.reduce((s, ad) => s + Number(ad.payment_amount ?? 0), 0), [filteredAds]);
  const bySection     = useMemo(() => groupByRevenue(filteredAds, (ad) => ad.ad_type?.title  || ad.ad_type?.name),  [filteredAds]);
  const byCategory    = useMemo(() => groupByRevenue(filteredAds, (ad) => ad.ad_category?.title || ad.ad_category?.name), [filteredAds]);
  const byCountry     = useMemo(() => groupByRevenue(filteredAds, (ad) => {
    const c = ad.user?.country;
    return typeof c === 'object' ? c?.name : c;
  }), [filteredAds]);
  const byCity        = useMemo(() => groupByRevenue(filteredAds, (ad) => {
    const c = ad.user?.city;
    return typeof c === 'object' ? c?.name : c;
  }), [filteredAds]);

  const loading = isLoading;

  return (
    <>
      <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
      {isFetching && !isLoading && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 9999, background: 'rgba(57,106,84,0.15)' }}>
          <div style={{ height: '100%', background: '#396A54', animation: 'shimmer 1.2s infinite', backgroundSize: '200% 100%', backgroundImage: 'linear-gradient(90deg, #396A54 0%, #5aaf84 50%, #396A54 100%)' }} />
        </div>
      )}
      <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
        <div className="d-flex flex-column flex-column-fluid">

          {/* Toolbar */}
          <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
            <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
              <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                  Financial Reports
                </h1>
                <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                  <li className="breadcrumb-item text-muted"><span className="text-muted">Home</span></li>
                  <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                  <li className="breadcrumb-item text-muted">Reports &amp; Stats</li>
                  <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                  <li className="breadcrumb-item text-muted">Financial Reports</li>
                </ul>
              </div>
              <div>
                <button
                  className="btn btn-sm btn-light-primary fw-semibold"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  title="Refresh data"
                >
                  <FontAwesomeIcon icon={faSyncAlt} className={isFetching ? 'fa-spin' : ''} />
                  <span className="ms-2">Refresh</span>
                </button>
              </div>
            </div>
          </div>

          <div id="kt_app_content" className="app-content flex-column-fluid">
            <div id="kt_app_content_container" className="app-container container-xxl">

              {/* Filter Bar */}
              <div className="card mb-6">
                <div className="card-body py-5">
                  <div className="row g-4 align-items-end">
                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">Country</label>
                      <select
                        className="form-select form-select-solid"
                        value={countryId}
                        onChange={(e) => { setCountryId(e.target.value); setCityId(''); }}
                        disabled={loading}
                      >
                        <option value="">All countries</option>
                        {countries.map((c: any) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">City</label>
                      <select
                        className="form-select form-select-solid"
                        value={cityId}
                        onChange={(e) => setCityId(e.target.value)}
                        disabled={loading}
                      >
                        <option value="">All cities</option>
                        {cities.map((c: any) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold text-muted fs-7 text-uppercase mb-2">Period</label>
                      <select
                        className="form-select form-select-solid"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        disabled={loading}
                      >
                        {PERIOD_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Stat Cards */}
              <div className="row g-5 mb-6" style={{ opacity: isFetching && !loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                <StatCard
                  label="Gross Revenue"
                  value={formatCurrency(grossRevenue)}
                  icon={faSackDollar}
                  iconColor="#22c55e"
                  loading={loading}
                />
                <StatCard
                  label="Ad Types / Sections"
                  value={String(bySection.length)}
                  icon={faLayerGroup}
                  iconColor="#396A54"
                  loading={loading}
                />
                <StatCard
                  label="Ad Categories"
                  value={String(byCategory.length)}
                  icon={faTag}
                  iconColor="#3b82f6"
                  loading={loading}
                />
                <StatCard
                  label="Countries"
                  value={String(byCountry.length)}
                  icon={faGlobe}
                  iconColor="#8b5cf6"
                  loading={loading}
                />
              </div>

              {/* Revenue Tables */}
              <RevenueTable
                title="Revenue by Ad Type / Section"
                subtitle="Gross advertising revenues broken down by ad type"
                icon={faLayerGroup}
                rows={bySection}
                emptyText="No section data available for this period"
                loading={loading}
              />
              <RevenueTable
                title="Revenue by Category"
                subtitle="Gross advertising revenues broken down by ad category"
                icon={faTag}
                rows={byCategory}
                emptyText="No category data available for this period"
                loading={loading}
              />
              <RevenueTable
                title="Revenue by Country"
                subtitle="Gross advertising revenues broken down by advertiser country"
                icon={faGlobe}
                rows={byCountry}
                emptyText="No country data available for this period"
                loading={loading}
              />
              <RevenueTable
                title="Revenue by City"
                subtitle="Gross advertising revenues broken down by advertiser city"
                icon={faCity}
                rows={byCity}
                emptyText="No city data available for this period"
                loading={loading}
              />

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminFinancialReport;
